import React, { useEffect, useState, memo, useRef } from "react";
import {
    TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogTitle,
    DialogActions, DialogContent, InputAdornment
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import InfoButton from "../../../components/Tooltip/InfoTextField";
import DefaultActions from "../../../components/Card/DefaultActions";
import { UserViewModel, UserRoles } from "../../../Types/ExportTypes";
import {
    LanguageCultureAutoComplete, PrinterAutoComplete
} from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const UserEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { id = null, loadMyUser = false } = params || {};
    const parsedLoadMyUser = loadMyUser === "false" ? false : loadMyUser === "true" ? true : Boolean(loadMyUser ?? false);
    const [isLoading, setIsLoading] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { navigate, goBack } = useNavigation();
    const [viewModel, setViewModel] = useState<(UserViewModel) | null>(null);
    const [showPrinterSettings, setShowPrinterSettings] = useState(false);
    const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
    const { t } = useTranslation();

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    useEffect(() => {
        isLoading ? showLoading() : hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    useEffect(() => {
        if (id) {
            loadUser(id);
        }
        else if (parsedLoadMyUser) {
            fetchMyuser();
        }
        else {
            const defaultUser: UserViewModel & { originalValues: Partial<UserViewModel>; hasChanges: boolean } = {
                createNew: true,
                originalValues: {},
                hasChanges: false,
            };
            defaultUser.originalValues = { ...defaultUser };
            setViewModel(defaultUser);
        }
    }, [id, authService, parsedLoadMyUser]);

    const loadUser = (id: any) => {
        setIsLoading(true);
        authService
            .get<UserViewModel>(`/userApi/GetUser?userId=${id}`)
            .then((response) =>
                setViewModel(
                    response
                        ? { ...response, originalValues: { ...response }, hasChanges: false }
                        : null
                )
            )
            .catch((error: any) => {
                showSnackbar(`${t("dialog.failedToFetchData")}: ${error.response?.data ?? error.message}`, "error");
            })
            .finally(() => setIsLoading(false));
    }

    const fetchMyuser = async () => {
        setIsLoading(true);
        try {
            const response = await authService.get<{ userId: number }>("/userApi/GetMyUserId");
            setIsLoading(false);
            loadUser(response.userId);

        } catch (error: any) {
            showSnackbar(`${t("dialog.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
            setIsLoading(false);
        }


    }

    const handleSave = async () => {
        if (!viewModel) return;
        if (!validateUser()) return;
        const hasChangedLanguage = viewModel.languageCultureName !== (viewModel.originalValues?.languageCultureName || "");
        setIsLoading(true);
        try {
            const createNew = viewModel.createNew;
            const response = await authService.post<UserViewModel>("/userApi/CreateOrUpdateUser", viewModel);
            setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(`${t("dialog.savedSuccessfully")}`, "success");
            if (hasChangedLanguage && viewModel.languageCultureName) {
                await authService.post("/userApi/SetLanguageAndCulture", { culture: viewModel.languageCultureName })
                const lang = (viewModel.languageCultureName || "").split("-")[0].toLowerCase();  // -> "en" or "sv"

                localStorage.setItem("lang", lang)
                localStorage.setItem("culture", viewModel.languageCultureName)
            }
            if (createNew === true) {
                navigate({ path: "/admin/user/edit", params: { id: response.user_id } });
            }
            if (response.reloadRoles) await reloadRoles();
        } catch {
            showSnackbar(`${t("dialog.failedToSaveChanges")}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const reloadRoles = async () => {
        const response = await authService.get<UserRoles>("/userApi/GetRoles");
        if (response) localStorage.setItem("roles", JSON.stringify({ roles: response }));
        window.location.reload();
    }
    const handleChange = (field: keyof UserViewModel, value: any) => {
        setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
    };

    const handleCancelChanges = () => {
        setViewModel((prev) => {
            if (!prev || !prev.originalValues) return prev;
            const original = prev.originalValues as UserViewModel;
            return { ...original, originalValues: { ...original }, hasChanges: false };
        });
    };

    const validateUser = () => {
        if (!viewModel) {
            showSnackbar(t("dialog.userMissing"), "error");
            return false;
        }
        if (!viewModel.user_name || viewModel.user_name.trim() === "") {
            showSnackbar(t("dialog.nameRequired"), "warning");
            return false;
        }

        if (!viewModel.user_username || viewModel.user_username.trim() === "") {
            showSnackbar(t("dialog.usernameRequired"), "warning");
            return false;
        }

        if (!viewModel.user_email || viewModel.user_email.trim() === "") {
            showSnackbar(t("dialog.emailRequired"), "warning");
            return false;
        }

        if (viewModel.createNew && (!viewModel.user_password || viewModel.user_password.trim() === "")) {
            showSnackbar(t("dialog.passwordRequired"), "warning");
            return false;
        }

        return true;
    };


    const handleCopy = async (newUserName?: string, newPassword?: string) => {
        if (viewModel?.hasChanges) showHasChangesError();
        var newItem = viewModel?.createNew ?? false;
        if (newItem) {
            showSnackbar(t("dialog.cannotCopyPreliminary"), "warning");
            return;
        }

        if (newUserName && newUserName === viewModel?.user_username) {
            showSnackbar(t("dialog.usernameSameAsSource"), "warning");
            return;
        }
        if (newUserName && newPassword) {

            setViewModel(prev => ({ ...prev, newUserName, newPassword }));
        }

        // If newCode isn't set, open the modal and return
        if ((!viewModel?.newUserName && !newUserName) || (!viewModel?.newPassword && !newPassword)) {
            setIsCopyModalOpen(true);
            return;
        }
        setIsCopyModalOpen(false);
        setIsLoading(true);
        try {
            var response = await authService.post<UserViewModel>(`/userApi/CopyUser`, { ...viewModel, newUserName, newPassword });
            setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(t("dialog.userCopied"), "success");

            navigate({ path: "/admin/user/edit", params: { id: response.user_id } });

        } catch (error: any) {

            showSnackbar(`${t("common.failedToCopy")} : ${error.response?.data ?? error.message}`, "error");
            setViewModel(prev => ({ ...prev, newCode: null, newPassword: null }));

        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (viewModel?.hasChanges) showHasChangesError();

        const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
        if (!isConfirmed) return;
        var newItem = viewModel?.createNew ?? false;
        if (newItem) {
            navigate({ path: "/admin/user/list" });
            return;
        }

        setIsLoading(true);
        try {
            await authService.post(`/userApi/DeleteUser`, viewModel);
            showSnackbar(t("dialog.userDeleted"), "success");

            navigate({ path: "/admin/user/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.failedDelete")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };





    type CopyUserModalProps = {
        open: boolean;
        onClose: () => void;
        onConfirm: (newCode: string, newPassword: string) => void;
        ViewModel?: UserViewModel | null
    };
    const CopyUserInputModal = memo(({ open, onClose, onConfirm, ViewModel }: CopyUserModalProps) => {
        const [tempUserName, setTempUserName] = useState("");
        const [tempPassword, setTempPassword] = useState("");
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>{t("common.enterNewUser")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("common.newUsername")}
                        fullWidth
                        variant="outlined"
                        value={tempUserName}
                        onChange={(e) => setTempUserName(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                        error={!tempUserName || tempUserName.trim() === "" || ViewModel?.user_username === tempUserName}
                        helperText={
                            ViewModel?.user_username === tempUserName
                                ? t("dialog.usernameSameAsSource")
                                : (!tempUserName || tempUserName.trim() === "")
                                    ? t("dialog.usernameRequired")
                                    : ""
                        }
                    />
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("common.newPassword")}
                        fullWidth
                        type="password"
                        variant="outlined"
                        value={tempPassword}
                        onChange={(e) => setTempPassword(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                        error={!tempPassword || tempPassword.trim() === ""}
                        helperText={!tempPassword || tempPassword.trim() === "" ? t("dialog.passwordRequired") : ""}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => {
                            if (!tempUserName.trim()) {
                                showSnackbar(t("dialog.usernameRequired"), "warning");
                                return;
                            }
                            if (!tempPassword.trim()) {
                                showSnackbar(t("dialog.passwordRequired"), "warning");
                                return;
                            }
                            onConfirm(tempUserName?.trim(), tempPassword?.trim()); // Pass only on confirm
                        }}
                        color="primary"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        );
    });

    return (
        <>
            <CenteredCard
                ref={toolbarRef}
                title={t("common.editUser")}
                isLoading={isLoading}
                handleSave={handleSave}
                handleCancel={handleCancelChanges}
                hasChanges={viewModel?.hasChanges ?? false}
            >
                <Box sx={{ display: "flex", justifyContent: "flex-start", maxWidth: "fit-content", mb: 2 }}>
                    <DefaultActions onSave={handleSave}
                        onDelete={viewModel?.editingUserIsAdmin ? handleDelete : undefined}
                        onCopy={viewModel?.editingUserIsAdmin ? handleCopy : undefined} />
                </Box>
                <CopyUserInputModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} ViewModel={viewModel} />
                {viewModel && (
                    <Grid container spacing={2}>

                        <Grid item xs={12}>
                            <TextField
                                label={t("common.name")}
                                value={viewModel.user_name || ""}
                                onChange={(e) => handleChange("user_name", e.target.value)}
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 255 }}
                                error={!viewModel.user_name || viewModel.user_name.trim() === ""}
                                helperText={
                                    !viewModel.user_name || viewModel.user_name.trim() === ""
                                        ? t("help.nameRequired")
                                        : ""
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label={t("common.username")}
                                value={viewModel.user_username || ""}
                                onChange={(e) => handleChange("user_username", e.target.value)}
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 255 }}
                                error={!viewModel.user_username || viewModel.user_username.trim() === ""}
                                helperText={
                                    !viewModel.user_username || viewModel.user_username.trim() === ""
                                        ? t("help.usernameRequired")
                                        : ""
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoButton infoText={t("help.usernameInfo")} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("common.email")}
                                value={viewModel.user_email || ""}
                                onChange={(e) => handleChange("user_email", e.target.value)}
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 255 }}
                                error={!viewModel.user_email || viewModel.user_email.trim() === ""}
                                helperText={
                                    !viewModel.user_email || viewModel.user_email.trim() === ""
                                        ? t("dialog.emailRequired")
                                        : ""
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoButton infoText={t("help.emailInfo")} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label={t("common.password")}
                                value={viewModel.user_password || ""}
                                onChange={(e) => handleChange("user_password", e.target.value)}
                                fullWidth
                                margin="normal"
                                type="password"
                                inputProps={{ maxLength: 255 }}
                                error={viewModel.createNew === true && (!viewModel.user_username || viewModel.user_username.trim() === "")}
                                helperText={
                                    viewModel.createNew === true &&
                                        (!viewModel.user_username || viewModel.user_username.trim() === "")
                                        ? t("help.passwordRequired")
                                        : ""
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoButton infoText={t("help.passwordInfo")} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>


                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <LanguageCultureAutoComplete
                                    key={viewModel.languageCultureName || undefined}
                                    valueId={viewModel.languageCultureName || null}
                                    onChange={(selected) => handleChange("languageCultureName", selected?.value || "")}
                                    infoText={t("help.defaultLanguage")}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!viewModel.admin}
                                        onChange={(e) => handleChange("admin", e.target.checked)}
                                        disabled={!viewModel.createNew && !viewModel.editingUserIsAdmin}
                                    />
                                }
                                label={t("common.admin")}
                            />
                        </Grid>


                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={showPrinterSettings} onChange={(e) => setShowPrinterSettings(e.target.checked)} />}
                                label={t("common.printers")}
                            />
                        </Grid>


                        {showPrinterSettings && (
                            <>
                                <Box
                                    sx={{
                                        backgroundColor: "secondary.main",
                                        padding: 2,
                                        borderRadius: 2,
                                        width: "100%"
                                    }}
                                >

                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="normal">
                                            <PrinterAutoComplete
                                                key={viewModel.user_LabelPrinterId || undefined}
                                                valueId={viewModel.user_LabelPrinterId || null}
                                                onChange={(selected) => handleChange("user_LabelPrinterId", selected?.value || "")}
                                                label={t("common.itemLabelPrinter")}
                                                infoText={t("help.itemLabelPrinter")}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="normal">
                                            <PrinterAutoComplete
                                                key={viewModel.user_ShipmentPrinterId || undefined}
                                                valueId={viewModel.user_ShipmentPrinterId || null}
                                                onChange={(selected) => handleChange("user_ShipmentPrinterId", selected?.value || "")}
                                                label={t("common.shipmentLabelPrinter")}
                                                infoText={t("help.shipmentLabelPrinter")}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="normal">
                                            <PrinterAutoComplete
                                                key={viewModel.user_AddressPrinterId || undefined}
                                                valueId={viewModel.user_AddressPrinterId || null}
                                                onChange={(selected) => handleChange("user_AddressPrinterId", selected?.value || "")}
                                                label={t("common.addressLabelPrinter")}
                                                infoText={t("help.addressLabelPrinter")}
                                            />
                                        </FormControl>
                                    </Grid>


                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="normal">
                                            <PrinterAutoComplete
                                                key={viewModel.user_A4PrinterId || undefined}
                                                valueId={viewModel.user_A4PrinterId || null}
                                                onChange={(selected) => handleChange("user_A4PrinterId", selected?.value || "")}
                                                label={t("common.a4Printer")}
                                                infoText={t("help.a4Printer")}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="normal">
                                            <PrinterAutoComplete
                                                key={viewModel.user_A5PrinterId || undefined}
                                                valueId={viewModel.user_A5PrinterId || null}
                                                onChange={(selected) => handleChange("user_A5PrinterId", selected?.value || "")}
                                                label={t("common.a5Printer")}
                                                infoText={t("help.a5Printer")}
                                            />
                                        </FormControl>
                                    </Grid>


                                </Box>
                            </>
                        )}



                    </Grid>
                )}

          <Box sx={{ marginTop: 2, textAlign: "center" }}>
            <Button variant="contained" onClick={handleSave} disabled={isLoading || !viewModel?.hasChanges} sx={{ marginRight: 2 }}>
              {t("common.save")}
            </Button>
            <Button variant="outlined" onClick={handleCancelChanges} disabled={!viewModel?.hasChanges} sx={{ marginRight: 2 }}>
              {t("common.cancel")}
            </Button>
            <Button variant="outlined" onClick={goBack}>
              {t("common.back")}
            </Button>
          </Box>
            </CenteredCard>

          
        </>
    );
};

export default UserEditPage;
